/* eslint-disable react-refresh/only-export-components */
import { Fragment, type ComponentProps, type PropsWithChildren } from 'react';
import * as ClerkReact from '@clerk/clerk-react';
import type { ClerkProviderProps } from '@clerk/clerk-react';

const hasPublishableKey = typeof import.meta.env.VITE_CLERK_PUBLISHABLE_KEY === 'string' &&
  import.meta.env.VITE_CLERK_PUBLISHABLE_KEY.length > 0;
const shouldMockAuth = import.meta.env.VITE_E2E === 'true' || !hasPublishableKey;

/**
 * NOTE: This mock layer keeps Playwright E2E tests unblocked while Clerk is not
 * wired into CI. Replace with real Clerk auth for tests once integration is ready.
 */

type UseClerkReturn = ReturnType<typeof ClerkReact.useClerk>;
type SignInProps = ComponentProps<typeof ClerkReact.SignIn>;
type SignUpProps = ComponentProps<typeof ClerkReact.SignUp>;
type SignInButtonProps = ComponentProps<typeof ClerkReact.SignInButton>;

// Mock components
const MockClerkProvider = ({ children }: ClerkProviderProps) => <>{children}</>;
const MockSignedIn: React.FC<PropsWithChildren> = () => null;
const MockSignedOut = ({ children }: PropsWithChildren) => <>{children}</>;
const MockSignInButton: React.FC<ComponentProps<typeof ClerkReact.SignInButton>> = ({ children }) => <>{children}</>;
const MockSignIn: React.FC<ComponentProps<typeof ClerkReact.SignIn>> = () => <Fragment />;
const MockSignUp: React.FC<ComponentProps<typeof ClerkReact.SignUp>> = () => <Fragment />;

// Mock hook implementation (must be a hook, not a factory function)
function useMockClerk(): UseClerkReturn {
  // This is a real hook (even though it doesn't use any React features)
  // React requires all functions starting with 'use' to follow hook rules
  return {
    signOut: async (options?: { redirectUrl?: string }) => {
      if (options?.redirectUrl && typeof window !== 'undefined') {
        window.location.href = options.redirectUrl;
      }
    },
    openUserProfile: () => {
      // Real Clerk opens an in-app modal. The mock no-ops so tests + the
      // local dev fallback don't crash; consumers should still defer to
      // useClerk().openUserProfile?.() to handle this branch.
    },
  } as UseClerkReturn;
}

// Component exports
export const ClerkProvider: React.FC<ClerkProviderProps> = (props) =>
  shouldMockAuth ? <MockClerkProvider {...props} /> : <ClerkReact.ClerkProvider {...props} />;

export const SignedIn: React.FC<PropsWithChildren> = (props) =>
  shouldMockAuth ? <MockSignedIn {...props} /> : <ClerkReact.SignedIn {...props} />;

export const SignedOut: React.FC<PropsWithChildren> = (props) =>
  shouldMockAuth ? <MockSignedOut {...props} /> : <ClerkReact.SignedOut {...props} />;

export const SignInButton: React.FC<SignInButtonProps> = (props) =>
  shouldMockAuth ? <MockSignInButton {...props} /> : <ClerkReact.SignInButton {...props} />;

export const SignIn: React.FC<SignInProps> = (props) =>
  shouldMockAuth ? <MockSignIn {...props} /> : <ClerkReact.SignIn {...props} />;

export const SignUp: React.FC<SignUpProps> = (props) =>
  shouldMockAuth ? <MockSignUp {...props} /> : <ClerkReact.SignUp {...props} />;

/**
 * Hook export that switches between mock and real implementation.
 *
 * IMPORTANT: This pattern satisfies Rules of Hooks because:
 * 1. The assignment happens at module evaluation time (not render time)
 * 2. isE2E is a compile-time constant from import.meta.env
 * 3. Both branches return ACTUAL HOOKS with identical signatures
 * 4. The dead branch is eliminated by the bundler in production
 *
 * Both useMockClerk and ClerkReact.useClerk are real hooks that can be
 * called during component render, satisfying React's hook requirements.
 */
export const useClerk: () => UseClerkReturn = shouldMockAuth ? useMockClerk : ClerkReact.useClerk;

type UseAuthReturn = ReturnType<typeof ClerkReact.useAuth>;

function useMockAuth(): UseAuthReturn {
  return {
    isLoaded: true,
    isSignedIn: false,
    userId: null,
    sessionId: null,
    sessionClaims: null,
    actor: null,
    orgId: null,
    orgRole: null,
    orgSlug: null,
    signOut: async () => {},
    getToken: async () => null,
    has: () => false,
  } as UseAuthReturn;
}

export const useAuth: () => UseAuthReturn = shouldMockAuth ? useMockAuth : ClerkReact.useAuth;

type UseUserReturn = ReturnType<typeof ClerkReact.useUser>;

/**
 * Mock useUser returns a static demo user. Real branch passes through to
 * Clerk. The Settings page uses this for read-only display of name +
 * email + photo. The mock is intentionally minimal — extend with more
 * Clerk User fields as new consumers need them.
 */
function useMockUser(): UseUserReturn {
  return {
    isLoaded: true,
    isSignedIn: true,
    user: {
      id: 'user_mock_local',
      fullName: 'Lincoln Yeung',
      firstName: 'Lincoln',
      lastName: 'Yeung',
      primaryEmailAddress: { emailAddress: 'demo@forecasthq.com' },
      emailAddresses: [{ emailAddress: 'demo@forecasthq.com' }],
      imageUrl: '',
      hasImage: false,
    },
  } as unknown as UseUserReturn;
}

export const useUser: () => UseUserReturn = shouldMockAuth ? useMockUser : ClerkReact.useUser;

export type { ClerkProviderProps };
